import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";


import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { forEach } from "jszip";

export default function Lookups(props) {
  const lookups = props.lookups || [];
  const [projectTypes, setProjectTypes] = useState([]);


  // GRID STATE
  const [columns, setColumns] = useState(["Col 1", "Col 2", "Col 3"]);
  const [rows, setRows] = useState([
    ["", "", ""],
    ["", "", ""],
    ["", "", ""],
  ]);

  const [lookupName, setLookupName] = useState("");
  const [assetTypeCode, setAssetTypeCode] = useState("");

  const [selectedSavedId, setSelectedSavedId] = useState(null);

  const [isFullscreen, setIsFullscreen] = useState(false);

  const [rowHeights, setRowHeights] = useState([]); // px per row, optional
  const dragInfoRef = useRef(null);

  useEffect(() => {
    if (projectTypes.length == 0 && lookups.length > 0) {
      const pType = lookups.find(item => item.lookupName === "PTYPE")
      if (pType?.data) {
        const pTypes = pType.data.rows.map(row =>
          Object.fromEntries(pType.data.columns.map((h, i) => [h, row[i]]))
        );
        setProjectTypes(pTypes || []);
      }
    }
  }, [lookups]);

  // SORT STATE
  const [sortState, setSortState] = useState({
    index: null,
    direction: null, // 'asc' | 'desc'
  });

  // DELETE CONFIRM
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    type: null, // 'row' | 'col'
    index: null,
  });

  // DELETE LOOKUP CONFIRM
  const [confirmDeleteSaved, setConfirmDeleteSaved] = useState({
    open: false,
    item: null,
  });

  // CSV IMPORT / EXPORT
  const fileInputRef = useRef(null);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [importError, setImportError] = useState("");

  // JSON SAVE / LOAD
  const [saveJsonDialogOpen, setSaveJsonDialogOpen] = useState(false);
  const [saveJsonText, setSaveJsonText] = useState("");
  const [loadJsonDialogOpen, setLoadJsonDialogOpen] = useState(false);
  const [loadJsonText, setLoadJsonText] = useState("");
  const [loadJsonError, setLoadJsonError] = useState("");

  // GROUP SAVED GRIDS BY ASSET TYPE FOR SIDEBAR
  const groupedSaved = useMemo(() => {
    const map = new Map();
    for (const item of lookups) {
      console.log(item);
      const code = item.assetTypeCode || "UNKNOWN";
      if (!map.has(code)) map.set(code, []);
      map.get(code).push(item);
    }
    return map;
  }, [lookups]);

  // ---------- CORE GRID HELPERS ----------

  const applyGridFromObject = (obj) => {
    if (!obj || typeof obj !== "object") {
      throw new Error("JSON must be an object.");
    }
    if (!Array.isArray(obj.columns) || !Array.isArray(obj.rows)) {
      throw new Error('JSON must have "columns" and "rows" arrays.');
    }

    let cols = obj.columns.map((c, i) =>
      c == null || c === "" ? `Col ${i + 1}` : String(c)
    );

    let rs = obj.rows.map((row) => {
      if (!Array.isArray(row)) {
        throw new Error("Each row must be an array.");
      }
      return row.map((v) => (v == null ? "" : String(v)));
    });

    const maxCols = Math.max(
      cols.length,
      rs.reduce((m, r) => Math.max(m, r.length), 0)
    );

    if (maxCols === 0) {
      throw new Error("No columns/rows found in JSON.");
    }

    if (cols.length < maxCols) {
      cols = [
        ...cols,
        ...Array.from(
          { length: maxCols - cols.length },
          (_, i) => `Col ${cols.length + i + 1}`
        ),
      ];
    } else if (cols.length > maxCols) {
      cols = cols.slice(0, maxCols);
    }

    rs = rs.map((r) => {
      if (r.length < maxCols) {
        return [...r, ...Array(maxCols - r.length).fill("")];
      } else if (r.length > maxCols) {
        return r.slice(0, maxCols);
      }
      return r;
    });

    setColumns(cols);
    setRows(rs);
    setSortState({ index: null, direction: null });

    if (typeof obj.lookupName === "string") setLookupName(obj.lookupName);
    if (typeof obj.assetTypeCode === "string")
      setAssetTypeCode(obj.assetTypeCode);
  };

  const buildGridJson = (nameOverride, assetCodeOverride) => ({
    columns,
    rows,
    lookupName: nameOverride ?? lookupName ?? "",
    assetTypeCode: assetCodeOverride ?? assetTypeCode ?? "",
  });

  const isLookupNameUnique = (name) => {
    const n = (name ?? "").trim();  // safe even if null or undefined
    if (!n) return false;
    return !lookups.some(
      (g) =>
        (g.lookupName ?? "")
          .trim()
          .toLowerCase() === n.toLowerCase() &&
        g.id !== selectedSavedId   // allow same project to keep its name
    );
  };

  const nameIsUnique = isLookupNameUnique(lookupName);

  // then compute full validity:
  const isLookupMetaValid =
    lookupName.trim().length > 0 &&
    assetTypeCode &&
    nameIsUnique;




  const startRowResize = (e, rowIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const rowElement = e.currentTarget.closest("tr");
    if (!rowElement) return;

    const startY = e.clientY;
    const startHeight =
      rowHeights[rowIndex] ?? rowElement.getBoundingClientRect().height;

    dragInfoRef.current = {
      rowIndex,
      startY,
      startHeight,
    };

    window.addEventListener("mousemove", handleRowResizeMove);
    window.addEventListener("mouseup", stopRowResize);
  };

  const handleRowResizeMove = (e) => {
    if (!dragInfoRef.current) return;
    const { rowIndex, startY, startHeight } = dragInfoRef.current;
    const delta = e.clientY - startY;
    const newHeight = Math.max(24, startHeight + delta); // min 24px

    setRowHeights((prev) => {
      const copy = [...prev];
      copy[rowIndex] = newHeight;
      return copy;
    });
  };

  const stopRowResize = () => {
    dragInfoRef.current = null;
    window.removeEventListener("mousemove", handleRowResizeMove);
    window.removeEventListener("mouseup", stopRowResize);
  };

  const autoSizeRow = (rowIndex) => {
    // Find the row in the DOM
    //console.log(rowIndex);
    const row = document.querySelector(`#lookup-grid-table tr[data-row="${rowIndex}"]`);
    if (!row) return;
    //console.log(row);
    // Find all textareas in that row
    const textareas = row.querySelectorAll("textarea");

    let maxHeight = 24; // minimum height

    textareas.forEach((ta) => {
      // we want the true natural content height
      ta.style.height = "auto"; // reset first
      const h = ta.scrollHeight;
      maxHeight = Math.max(maxHeight, h);
    });

    // Add some padding allowance
    const finalHeight = maxHeight + 12;

    setRowHeights((prev) => {
      const copy = [...prev];
      copy[rowIndex] = finalHeight;
      return copy;
    });
  };



  // ---------- GRID EDIT HANDLERS ----------

  const handleCellChange = (rowIndex, colIndex, value) => {
    setRows((prev) => {
      const copy = prev.map((r) => [...r]);
      copy[rowIndex][colIndex] = value;
      return copy;
    });
  };

  const handleHeaderChange = (colIndex, value) => {
    setColumns((prev) => {
      const copy = [...prev];
      copy[colIndex] = value;
      return copy;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, Array(columns.length).fill("")]);
  };

  const addColumn = () => {
    setColumns((prev) => [...prev, `Col ${prev.length + 1}`]);
    setRows((prev) => prev.map((row) => [...row, ""]));
  };


  /*
  const clearAll = () => {
    setRows([]);
    setColumns((prev) => prev.map(() => ""));
    setSortState({ index: null, direction: null });
  };
  */

  const clearAll = () => {
    setRows([]);
    setColumns([""]); // single empty column after clear
    setSortState({ index: null, direction: null });
  };


  const deleteRow = (index) => {
    setRows((rows) => rows.filter((_, i) => i !== index));
  };

  const deleteColumn = (index) => {
    setColumns((cols) => cols.filter((_, i) => i !== index));
    setRows((r) => r.map((row) => row.filter((_, i) => i !== index)));
    setSortState((prev) => {
      if (prev.index == null) return prev;
      if (prev.index === index) return { index: null, direction: null };
      if (prev.index > index) return { ...prev, index: prev.index - 1 };
      return prev;
    });
  };

  // ---------- SORT ----------

  const handleSortClick = (colIndex) => {
    setSortState((prevSort) => {
      let nextDirection;
      if (prevSort.index === colIndex && prevSort.direction === "asc") {
        nextDirection = "desc";
      } else {
        nextDirection = "asc";
      }

      setRows((prevRows) => {
        const copy = [...prevRows];
        copy.sort((a, b) => {
          const av = a[colIndex] ?? "";
          const bv = b[colIndex] ?? "";
          if (nextDirection === "asc") {
            return av.localeCompare(bv, undefined, {
              numeric: true,
              sensitivity: "base",
            });
          } else {
            return bv.localeCompare(av, undefined, {
              numeric: true,
              sensitivity: "base",
            });
          }
        });
        return copy;
      });

      return { index: colIndex, direction: nextDirection };
    });
  };

  const getSortIconForColumn = (colIndex) => {
    if (sortState.index !== colIndex || !sortState.direction) return null;
    return sortState.direction === "asc" ? (
      <ArrowUpwardIcon fontSize="inherit" />
    ) : (
      <ArrowDownwardIcon fontSize="inherit" />
    );
  };

  // ---------- DELETE CONFIRM ----------

  const startDeleteRow = (index) =>
    setConfirmDelete({ open: true, type: "row", index });

  const startDeleteColumn = (index) =>
    setConfirmDelete({ open: true, type: "col", index });

  const handleCloseConfirm = () =>
    setConfirmDelete({ open: false, type: null, index: null });

  const handleConfirmDelete = () => {
    if (confirmDelete.type === "row") deleteRow(confirmDelete.index);
    else if (confirmDelete.type === "col") deleteColumn(confirmDelete.index);
    handleCloseConfirm();
  };

  // ---------- CSV PARSE / IMPORT / EXPORT ----------
  const parseDelimited = (text) => {
    //const parseDelimited = (text, delimiter = ",") => {
    const delimiter = detectDelimiter(text);
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = normalized
      .split("\n")
      .map((l) => l.trimEnd())
      .filter((l) => l.length > 0);

    if (!lines.length) throw new Error("No data found");

    const parseLine = (line) => line.split(delimiter);

    const rowsParsed = lines.map(parseLine);
    const maxCols = rowsParsed.reduce(
      (max, row) => Math.max(max, row.length),
      0
    );
    if (maxCols === 0) throw new Error("No columns found");

    const headerRow = rowsParsed[0];
    const dataRows = rowsParsed.slice(1);

    const cols = Array.from({ length: maxCols }, (_, i) => {
      const val = headerRow[i] ?? "";
      return val || `Col ${i + 1}`;
    });

    const paddedRows = dataRows.map((r) =>
      Array.from({ length: maxCols }, (_, i) => r[i] ?? "")
    );

    return { columns: cols, rows: paddedRows };
  };

  const parseCsv = (text) => {
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = normalized
      .split("\n")
      .map((l) => l.trimEnd())
      .filter((l) => l.length > 0);

    if (!lines.length) throw new Error("No data found in CSV.");

    const parseLine = (line) => {
      const result = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          result.push(current);
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current);
      return result;
    };

    const rowsParsed = lines.map(parseLine);
    const maxCols = rowsParsed.reduce(
      (max, row) => Math.max(max, row.length),
      0
    );
    if (maxCols === 0) throw new Error("CSV appears to be empty.");

    const headerRow = rowsParsed[0];
    const dataRows = rowsParsed.slice(1);

    const cols = Array.from({ length: maxCols }, (_, i) => {
      const val = headerRow[i] ?? "";
      return val || `Col ${i + 1}`;
    });

    const paddedRows = dataRows.map((r) =>
      Array.from({ length: maxCols }, (_, i) => r[i] ?? "")
    );

    return { columns: cols, rows: paddedRows };
  };

  const detectDelimiter = (text) => {
    // Normalise newlines
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = normalized
      .split("\n")
      .map((l) => l.trimEnd())
      .filter((l) => l.length > 0);

    if (!lines.length) return ","; // fallback

    // Look at first few non-empty lines
    const sampleLines = lines.slice(0, 10);

    let commaCount = 0;
    let tabCount = 0;
    //let semicolonCount = 0;

    for (const line of sampleLines) {
      // crude counts; good enough for detection
      commaCount += (line.match(/,/g) || []).length;
      tabCount += (line.match(/\t/g) || []).length;
      //semicolonCount += (line.match(/;/g) || []).length;
    }

    if (tabCount > 0 && tabCount >= commaCount) { // && tabCount >= semicolonCount) {
      return "\t"; // TSV
    }
    /*
        if (semicolonCount > commaCount && semicolonCount > 0) {
          return ";"; // some locales use ; as CSV separator
        }
    */
    return ","; // default CSV
  };


  const importFromText = () => {
    try {
      const { columns: cols, rows: rs } = parseDelimited(pasteText);
      setColumns(cols);
      setRows(rs);
      setSortState({ index: null, direction: null });
      setImportError("");
      setPasteDialogOpen(false);
    } catch (err) {
      setImportError(err.message || "Failed to parse CSV.");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = String(event.target?.result || "");
        const { columns: cols, rows: rs } = parseDelimited(text);
        setColumns(cols);
        setRows(rs);
        setSortState({ index: null, direction: null });
        setImportError("");
      } catch (err) {
        setImportError(err.message || "Failed to parse CSV file.");
      } finally {
        e.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  const toCsv = (cols, rs) => {
    const quote = (value) => {
      const v = value ?? "";
      if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const headerLine = cols.map(quote).join(",");
    const rowLines = rs.map((row) => row.map(quote).join(","));
    return [headerLine, ...rowLines].join("\r\n");
  };

  const handleExportCsv = () => {
    const csv = toCsv(columns, rows);
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "grid.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---------- JSON SAVE / LOAD ----------




  const handleSaveJsonClick = () => {
    const gridJson = buildGridJson();
    setSaveJsonText(JSON.stringify(gridJson, null, 2));
    setSaveJsonDialogOpen(true);
  };

  const handleSaveJsonConfirm = () => {
    if (!isLookupMetaValid) return;
    const gridJson = buildGridJson();
    if (typeof props.saveLookup === "function") {
      //const lookup = {id: selectedSavedId, typeId, title, json}
      props.saveLookup(selectedSavedId, gridJson);
    }
    // keep dialog open so you can still copy if you want
    // or close it here if you prefer:
    setSaveJsonDialogOpen(false);
  };

  const handleLookupNameChange = (value) => {
    setLookupName(value);
    const gridJson = buildGridJson(value, undefined);
    setSaveJsonText(JSON.stringify(gridJson, null, 2));
  };

  const handleAssetTypeChange = (value) => {
    setAssetTypeCode(value);
    const gridJson = buildGridJson(undefined, value);
    setSaveJsonText(JSON.stringify(gridJson, null, 2));
  };

  const handleLoadJson = () => {
    try {
      const obj = JSON.parse(loadJsonText);
      applyGridFromObject(obj);
      setLoadJsonError("");
      setLoadJsonDialogOpen(false);
    } catch (err) {
      setLoadJsonError(err.message || "Failed to parse JSON.");
    }
  };

  // ---------- LOAD FROM SAVED ITEM (SIDEBAR) ----------

  const handleSelectSavedGrid = (item) => {
    console.log(item);
    try {
      const raw = item.gridJson ?? item.json ?? item.data;
      const obj =
        typeof raw === "string"
          ? JSON.parse(raw)
          : raw && typeof raw === "object"
            ? raw
            : null;
      if (!obj) throw new Error("Invalid grid JSON");
      applyGridFromObject(obj);
      setSelectedSavedId(item.id ?? null);
    } catch (err) {
      console.error("Failed to load saved grid", err);
    }
  };

  // ---------- CREATE NEW LOOKUP ----------


  const handleNewGrid = () => {
    // this makes the editor represent a brand new, unsaved grid
    setSelectedSavedId(null);
    //setLastLoadedGridJsonString(null); // no baseline -> no "Revert to saved" / dirty tracking

    // optional: reset metadata so user must set new name/type
    setLookupName("");
    setAssetTypeCode("");

    // OPTIONAL: if you want a totally blank grid, uncomment:
    setColumns([""]);
    setRows([]);
    setSortState({ index: null, direction: null });
  };

  // ---------- DELETE LOOKUP ----------




  const startDeleteSaved = (item) => {
    setConfirmDeleteSaved({ open: true, item });
  };

  const handleCloseDeleteSaved = () => {
    setConfirmDeleteSaved({ open: false, item: null });
  };

  const handleConfirmDeleteSaved = () => {

    const item = confirmDeleteSaved.item;
    if (!item) return handleCloseDeleteSaved();

    // call parent to delete
    if (typeof props.deleteLookup === "function") {
      props.deleteLookup(item);
    }



    // if deleted item was selected, clear selection
    console.log(selectedSavedId,item.id);
    if (selectedSavedId === item.id) {
      setSelectedSavedId(null);
    }

    handleCloseDeleteSaved();

  };


  // ---------- Paste csv text from clipboard ----------

  const handlePasteCsvInstant = async () => {
    try {
      const text = await navigator.clipboard.readText();

      if (!text || !text.trim()) {
        setImportError("Clipboard is empty.");
        return;
      }

      const { columns: cols, rows: rs } = parseDelimited(text);

      setColumns(cols);
      setRows(rs);
      setSortState({ index: null, direction: null });
      setImportError("");

    } catch (err) {
      setImportError(err.message || "Failed to read CSV from clipboard.");
    }
  };

  // ---------- RENDER ----------





  const gridContent = (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Box sx={{ p: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            overflowX: "auto",
            gap: 1,
            mb: 1,
          }}
        >
          <Stack direction="row"
            spacing={1}
            sx={{
              flexGrow: 1,
              overflowX: "auto",    // scroll if lots of buttons
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => fileInputRef.current?.click()}
            >
              Import file
            </Button>
            {/*}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPasteText("");
                setImportError("");
                setPasteDialogOpen(true);
              }}
            >
              Paste CSV
            </Button>
            */}

            <Button
              variant="outlined"
              size="small"
              onClick={handlePasteCsvInstant}
            >
              Paste
            </Button>


            <Button variant="outlined" size="small" onClick={handleExportCsv}>
              Export CSV
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={!selectedSavedId}
              onClick={() => {
                const item = lookups.find(x => x.id === selectedSavedId);
                if (item) startDeleteSaved(item);
              }}
            >
              Delete table
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveJsonClick}
            >
              Save
            </Button>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />




          {!isFullscreen &&
            <Button
              startIcon={<FullscreenIcon />}
              variant="outlined"
              size="small"
              onClick={() => setIsFullscreen(true)}
            >
              Fullscreen
            </Button>
          }

        </Box>




        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
          <Button variant="contained" size="small" onClick={addRow}>
            Add row
          </Button>

          <Button variant="outlined" size="small" onClick={addColumn}>
            Add column
          </Button>

          {/* CLEAR ALL */}
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={clearAll}
          >
            Clear all
          </Button>
        </Stack>








        {importError && (
          <Typography color="error" variant="body2" mb={1}>
            {importError}
          </Typography>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,text/csv/tsv"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {/* SCROLLABLE TABLE WRAPPER, WITH FROZEN FIRST COL */}
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            position: "relative", // important for sticky
          }}
        >
          <table
            id="lookup-grid-table"
            style={{
              borderCollapse: "collapse",
              minWidth: "max-content",
            }}
          >
            <thead>
              <tr>
                {columns.map((col, colIndex) => (
                  <th
                    key={colIndex}
                    style={{
                      border: "1px solid #ccc",
                      padding: "6px 10px",
                      background: "#f5f5f5",
                      whiteSpace: "nowrap",
                      // FROZEN FIRST COL HEADER
                      ...(colIndex === 0
                        ? {
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                        }
                        : {}),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <TextField
                        value={col}
                        onChange={(e) =>
                          handleHeaderChange(colIndex, e.target.value)
                        }
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          flexGrow: 1,
                          "& .MuiInputBase-input": {
                            fontWeight: 600,
                            padding: "2px 0",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />

                      <IconButton
                        size="small"
                        onClick={() => handleSortClick(colIndex)}
                      >
                        {getSortIconForColumn(colIndex) || (
                          <ArrowUpwardIcon
                            fontSize="inherit"
                            sx={{ opacity: 0.3 }}
                          />
                        )}
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => startDeleteColumn(colIndex)}
                        disabled={columns.length <= 1}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </th>
                ))}

                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "6px 10px",
                    background: "#f5f5f5",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    width: 80,
                  }}
                >

                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, rowIndex) => {
                const rowHeight = rowHeights[rowIndex]; // may be undefined
                return (
                  <tr
                    key={rowIndex}
                    data-row={rowIndex}
                    style={{
                      height: rowHeights[rowIndex]
                        ? `${rowHeights[rowIndex]}px`
                        : "auto",
                    }}
                  >
                    {row.map((cell, colIndex) => {
                      const isFrozen = colIndex === 0;
                      return (
                        <td
                          key={colIndex}
                          data-col={colIndex}
                          style={{
                            border: "1px solid #ccc",
                            padding: "6px 10px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            maxWidth: 280,
                            verticalAlign: "top",
                            position: isFrozen ? "sticky" : "static",
                            left: isFrozen ? 0 : undefined,
                            zIndex: isFrozen ? 2 : 1,
                            background: isFrozen ? "#fff" : "#f8f8ff",
                          }}
                        >
                          <textarea
                            value={cell ?? ""}
                            onChange={(e) =>
                              handleCellChange(rowIndex, colIndex, e.target.value)
                            }
                            style={{
                              width: "100%",
                              // 🔻 if we have a rowHeight, fill (rowHeight - padding)
                              height: rowHeight ? `${rowHeight - 12}px` : "auto",
                              border: "none",
                              outline: "none",
                              resize: "none", // no per-cell resize
                              padding: 0,
                              margin: 0,
                              font: "inherit",
                              background: "transparent",
                              display: "block",
                              boxSizing: "border-box",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              overflow: "auto",
                            }}
                          />

                          {/* row drag handle in first col only */}
                          {isFrozen && (
                            <div
                              onMouseDown={(e) => startRowResize(e, rowIndex)}
                              onDoubleClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                autoSizeRow(rowIndex);
                              }}

                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: 6,
                                cursor: "row-resize",
                                background:
                                  "linear-gradient(to right, transparent 0%, #ccc 50%, transparent 100%)",
                              }}
                            />
                          )}
                        </td>
                      );
                    })}

                    {/* actions cell (unchanged) */}
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "6px 10px",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        verticalAlign: "top",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => startDeleteRow(rowIndex)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </td>

                    {/* row resize handle cell (optional) */}
                    {/* Or we can put handle in the first cell; see below */}
                  </tr>
                );
              })}
            </tbody>

          </table>
        </Box>
      </Box>
    </Box>

  );




  return (
    <Box p={0} sx={{ display: "flex", height: "100%" }}>
      {/* LEFT SIDEBAR */}
      <Box
        sx={{
          minWidth: 160,
          borderRight: "1px solid #ddd",
          p: 2,
          bgcolor: "#fafafa",
          overflowY: "auto",
        }}
      >
        {/*}
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Lookups
        </Typography>
        <Typography variant="caption" color="text.secondary">
          by project type
        </Typography>
        */}
        <Box mt={0} mb={2}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={handleNewGrid}
          >
            New lookup
          </Button>
        </Box>
        <Box mt={2}>
          {projectTypes.map((type) => {
            const list = groupedSaved.get(type.code) || [];
            if (!list.length) return null;
            return (
              <Box key={type.code} mb={2}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{ fontSize: "0.8rem", textTransform: "uppercase" }}
                >
                  {type.title}
                </Typography>
                <Stack spacing={0.5}>
                  {list.map((item) => (
                    <Button
                      key={item.id ?? item.lookupName}
                      variant={
                        selectedSavedId === item.id ? "contained" : "text"
                      }
                      size="small"
                      fullWidth
                      onClick={() => handleSelectSavedGrid(item)}
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        paddingInline: 1,
                      }}
                    >
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="body2"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {item.lookupName || "(untitled)"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {type.code}
                        </Typography>
                      </Box>
                    </Button>
                  ))}
                </Stack>
              </Box>
            );
          })}

          {groupedSaved.get("UNKNOWN")?.length > 0 && (
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                gutterBottom
                sx={{ fontSize: "0.8rem", textTransform: "uppercase" }}
              >
                Other
              </Typography>
              <Stack spacing={0.5}>
                {groupedSaved.get("UNKNOWN").map((item) => (
                  <Button
                    key={item.id ?? item.lookupName}
                    variant={
                      selectedSavedId === item.id ? "contained" : "text"
                    }
                    size="small"
                    fullWidth
                    onClick={() => handleSelectSavedGrid(item)}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      paddingInline: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {item.lookupName || "(untitled)"}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      {/* RIGHT SIDE: GRID + CONTROLS */}
      {!isFullscreen && gridContent}

      {/* CONFIRM DELETE DIALOG */}
      <Dialog open={confirmDelete.open} onClose={handleCloseConfirm}>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDelete.type === "row"
              ? "Delete this row?"
              : confirmDelete.type === "col"
                ? "Delete this column?"
                : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* PASTE CSV DIALOG */}
      <Dialog
        open={pasteDialogOpen}
        onClose={() => setPasteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Paste CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            Paste CSV text here (first row will be treated as headers).
          </Typography>
          <TextField
            multiline
            minRows={6}
            fullWidth
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
          {importError && (
            <Typography color="error" variant="body2" mt={1}>
              {importError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={importFromText}>
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* SAVE JSON DIALOG */}
      <Dialog
        open={saveJsonDialogOpen}
        onClose={() => setSaveJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Save Lookup</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mb={2} mt={2}>
            <TextField
              label="Lookup name"
              fullWidth
              disabled={selectedSavedId}
              value={lookupName}
              onChange={(e) => handleLookupNameChange(e.target.value)}










              error={!nameIsUnique}
              helperText={
                !nameIsUnique
                  ? "Project name must be unique."
                  : " "
              }




















            />

            <FormControl fullWidth>
              <InputLabel id="asset-type-label">Project type</InputLabel>
              <Select
                labelId="asset-type-label"
                label="Project type"
                value={assetTypeCode}
                onChange={(e) => handleAssetTypeChange(e.target.value)}
              >
                {projectTypes.map((a) => (
                  <MenuItem key={a.code} value={a.code}>
                    {a.title} – {a.subtitle} ({a.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {/*}
          <Typography variant="body2" mb={1}>
            JSON (includes lookupName and assetTypeCode):
          </Typography>
          <TextField
            multiline
            minRows={8}
            fullWidth
            value={saveJsonText}
            InputProps={{ readOnly: true }}
          />
          */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveJsonDialogOpen(false)}>Close</Button>
          <Button variant="contained"
            disabled={!isLookupMetaValid}
            onClick={handleSaveJsonConfirm}


          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* LOAD JSON DIALOG */}

      <Dialog fullScreen open={isFullscreen} onClose={() => setIsFullscreen(false)}>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => setIsFullscreen(false)}>Close Fullscreen View</Button>
        </Box>
        {gridContent}
      </Dialog>

      <Dialog
        open={confirmDeleteSaved.open}
        onClose={handleCloseDeleteSaved}
      >
        <DialogTitle>Delete saved table</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{confirmDeleteSaved.item?.lookupName || "(untitled)"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteSaved}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDeleteSaved}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={loadJsonDialogOpen}
        onClose={() => setLoadJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Load grid from JSON</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            Paste JSON with the shape:
            <br />
            {`{ "columns": [...], "rows": [[...], ...], "lookupName": "...", "assetTypeCode": "..." }`}
          </Typography>
          <TextField
            multiline
            minRows={8}
            fullWidth
            value={loadJsonText}
            onChange={(e) => setLoadJsonText(e.target.value)}
          />
          {loadJsonError && (
            <Typography color="error" variant="body2" mt={1}>
              {loadJsonError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadJsonDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLoadJson}>
            Load
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
}
